import { useCallback, useEffect, useRef, useState } from 'react';
import {
  panelSideNote,
  codeFromQrKey,
  EquipmentInput,
  FILE_KIND_LABEL,
  qrKeyFromCode,
  type AreaDto,
  type EquipmentDetailDto,
  type EquipmentFileDto,
  type EquipmentFileKind,
  type PanelDto,
  type CheckDto,
  type Procedure,
  type ProcedureItem,
} from '@app/shared';
import { ProceduresEditor } from './ProceduresEditor';
import { api, ApiError } from '../api';
import { platform } from '../platform';
import { useApp } from '../state';
import { bytes, clock, Icon, LinkStatus, Sheet } from '../ui';
import { SubHead } from './admin/SubHead';
import { areaPath } from './Panels';
import { PdfViewer } from './PdfViewer';
import { AuthImg, AuthVideo } from './AuthMedia';
import { StepContent, StepEditor, StepList } from './Steps';

type Load = { state: 'loading' } | { state: 'ok'; unit: EquipmentDetailDto } | { state: 'missing'; qr: string | null; message: string } | { state: 'new'; code: string; qr: string };

/**
 * Страница единицы оборудования: панели (подключиться), описание, инструкция, видео, фото, документы.
 * Открывается из вкладки «Оборудование» или по QR-коду на самом оборудовании.
 */
export function Unit({ id, qr }: { id?: string; qr?: string }) {
  const { fail, can, open, close, toast, me } = useApp();
  const [load, setLoad] = useState<Load>(id || qr ? { state: 'loading' } : { state: 'new', code: '', qr: '' });
  const [areas, setAreas] = useState<AreaDto[]>([]);
  const [editing, setEditing] = useState(!id && !qr);
  const [editLists, setEditLists] = useState<string | false>(false);
  const [step, setStep] = useState<{ proc: Procedure; item: ProcedureItem; n: number; edit: boolean } | null>(null);
  const [newFiles, setNewFiles] = useState<EquipmentFileDto[]>([]);
  const [starting, setStarting] = useState<string | null>(null);
  const [photo, setPhoto] = useState<EquipmentFileDto | null>(null);
  const [docOpen, setDocOpen] = useState<EquipmentFileDto | null>(null);
  const [upload, setUpload] = useState<{ name: string; share: number } | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const avatarInput = useRef<HTMLInputElement>(null);
  const manage = can('equipment.manage');

  const reload = useCallback(
    async (unitId?: string) => {
      try {
        const targetId = unitId ?? id ?? (qr ? (await api.equipmentByQr(qr)).id : null);
        if (!targetId) return;
        setLoad({ state: 'ok', unit: await api.equipmentDetail(targetId) });
      } catch (e) {
        if (e instanceof ApiError && e.status === 404) {
          if (qr && manage) setLoad({ state: 'new', code: codeFromQrKey(qr), qr: qr.toUpperCase() });
          else setLoad({ state: 'missing', qr: qr ?? null, message: e.message });
        } else fail(e);
      }
    },
    [id, qr, manage, fail],
  );

  useEffect(() => {
    void reload();
    api.areas().then(setAreas).catch(() => {});
  }, [reload]);

  async function pickFile(f: File | undefined, avatar = false) {
    if (!f || load.state !== 'ok') return;
    const title = avatar ? 'Главное фото' : f.name.replace(/\.[^.]+$/, '').slice(0, 160) || FILE_KIND_LABEL.doc;
    setUpload({ name: f.name, share: 0 });
    try {
      await api.uploadEquipmentFile(load.unit.id, f, title, (share) => setUpload({ name: f.name, share }), avatar);
      platform.haptic('success');
      toast(avatar ? 'Главное фото обновлено' : 'Файл добавлен');
      await reload(load.unit.id);
    } catch (e) {
      fail(e);
    } finally {
      setUpload(null);
      if (fileInput.current) fileInput.current.value = '';
      if (avatarInput.current) avatarInput.current.value = '';
    }
  }

  /** Пройти этап: продолжить свою незавершённую сегодняшнюю проверку или начать новую. */
  async function startCheck(p: Procedure) {
    if (load.state !== 'ok') return;
    setStarting(p.id);
    try {
      const c = await api.startCheck(load.unit.id, p.id);
      open({ name: 'check', id: c.id });
    } catch (e) {
      fail(e);
    } finally {
      setStarting(null);
    }
  }

  async function makeAvatar(f: EquipmentFileDto) {
    if (load.state !== 'ok') return;
    try {
      await api.updateEquipment(load.unit.id, { avatarFileId: f.id });
      platform.haptic('success');
      toast('Это фото теперь главное');
      setPhoto(null);
      await reload(load.unit.id);
    } catch (e) {
      fail(e);
    }
  }

  async function removeFile(f: EquipmentFileDto) {
    if (load.state !== 'ok' || !(await platform.confirm(`Удалить «${f.title}»?`))) return;
    try {
      await api.deleteEquipmentFile(load.unit.id, f.id);
      setPhoto(null);
      await reload(load.unit.id);
    } catch (e) {
      fail(e);
    }
  }

  if (load.state === 'loading') {
    return (
      <div className="stack">
        <SubHead title="Оборудование" />
        <div className="skeleton" />
        <div className="skeleton" />
      </div>
    );
  }

  if (load.state === 'missing') {
    return (
      <div className="stack">
        <SubHead title="Оборудование" />
        <div className="card empty">
          <span className="mark">
            <Icon name="info" />
          </span>
          {load.qr && <div className="h2">{codeFromQrKey(load.qr)}</div>}
          <p className="muted" style={{ margin: 0 }}>
            {load.message}
          </p>
          <button type="button" className="btn btn-outline" onClick={close}>
            К списку оборудования
          </button>
        </div>
      </div>
    );
  }

  if (load.state === 'new') {
    return (
      <div className="stack">
        <SubHead title="Новое оборудование" />
        {load.qr && (
          <div className="card flat small">
            QR-код {load.qr} ещё не привязан. Проверьте заводской номер и сохраните — после этого код на оборудовании будет открывать эту страницу.
          </div>
        )}
        <EditUnit
          unit={null}
          initial={{ code: load.code, qr: load.qr }}
          areas={areas}
          onClose={close}
          onSaved={(newId) => {
            setEditing(false);
            open({ name: 'unit', id: newId });
          }}
          inline
        />
      </div>
    );
  }

  const u = load.unit;
  // Файлы пунктов («как сделать») в общей галерее не показываем.
  const files = (kind: EquipmentFileKind) => u.files.filter((f) => f.kind === kind && !f.step);
  const allFiles = [...u.files, ...newFiles];

  /** Сохранить один пункт: заменить его в списке и отправить списки целиком. */
  async function saveStep(proc: Procedure, item: ProcedureItem) {
    try {
      const procedures = u.procedures.map((p) => (p.id === proc.id ? { ...p, items: p.items.map((i) => (i.id === item.id ? item : i)) } : p));
      await api.updateEquipment(u.id, { procedures });
      platform.haptic('success');
      toast('Пункт сохранён');
      setStep(null);
      await reload(u.id);
    } catch (e) {
      fail(e);
    }
  }

  return (
    <div className="stack">
      <SubHead title={u.name}>
        {manage && (
          <button type="button" className="icon-btn" aria-label="Изменить оборудование" onClick={() => setEditing(true)}>
            <Icon name="edit" size={18} />
          </button>
        )}
      </SubHead>

      {u.avatarUrl && (
        <button type="button" className="unit-hero" aria-label="Главное фото" onClick={() => setPhoto(u.files.find((f) => f.id === u.avatarFileId) ?? null)}>
          <AuthImg path={u.avatarUrl} alt={u.name} />
        </button>
      )}

      <div className="row wrap" style={{ gap: 8, marginTop: u.avatarUrl ? 0 : -6 }}>
        <span className="badge outline">{u.code}</span>
        <span className="small muted">{areaPath(areas, u.areaId)}</span>
      </div>

      {(u.panels.length > 0 || can('panel.view')) && (
        <section className="stack" style={{ gap: 8 }}>
          <h2 className="section-title">Панели</h2>
          {u.panels.length > 0 ? (
            <div className="list">
              {u.panels.map((p: PanelDto) => (
                <button key={p.id} type="button" className="item" onClick={() => open({ name: 'viewer', panelId: p.id })}>
                  <span className="mark">
                    <Icon name="panels" />
                  </span>
                  <span className="grow stack" style={{ gap: 3 }}>
                    <span className="title ellipsis">{p.name}</span>
                    <span className="row wrap" style={{ gap: '2px 8px', minWidth: 0 }}>
                      {panelSideNote(p, u.panels) && <small className="side-note">{panelSideNote(p, u.panels)}</small>}
                      <LinkStatus link={p.link} />
                    </span>
                  </span>
                  <span className="btn btn-primary btn-sm">
                    {p.canControl ? 'Подключиться' : 'Смотреть'}
                  </span>
                </button>
              ))}
            </div>
          ) : (
            <p className="small muted" style={{ margin: 0 }}>
              {manage ? 'Панели не привязаны — выберите их в настройках (карандаш вверху).' : 'Панелей у этого оборудования нет.'}
            </p>
          )}
        </section>
      )}

      {u.procedures.map((p) => {
        const today = u.checks.filter((c) => c.procedureId === p.id);
        const mineOpen = today.find((c) => c.status === 'open' && c.employeeId === me.employee?.id);
        const last = today.find((c) => c.status !== 'open');
        return (
          <section key={p.id} className="stack" style={{ gap: 8 }}>
            <div className="proc-head">
              <h2 className="section-title">{p.title}</h2>
              {p.kind === 'check' && last && <CheckBadge c={last} onOpen={() => open({ name: 'check', id: last.id })} />}
              {manage && (
                <button type="button" className="icon-btn" aria-label={`Изменить «${p.title}»`} onClick={() => setEditLists(p.id)}>
                  <Icon name="edit" size={16} />
                </button>
              )}
            </div>
            <StepList items={p.items} manage={manage} onOpen={(item, n) => setStep({ proc: p, item, n, edit: false })} />
            {p.kind === 'check' && can('panel.view') && (
              <button type="button" className={`btn btn-block ${mineOpen || !last ? 'btn-primary' : 'btn-outline'}`} disabled={starting === p.id} onClick={() => void startCheck(p)}>
                <Icon name="tasks" size={18} />
                {mineOpen ? 'Продолжить проверку' : last ? 'Пройти ещё раз' : 'Пройти проверку'}
              </button>
            )}
          </section>
        );
      })}

      {u.responsible && (
        <p className="small muted" style={{ margin: 0 }}>
          Ответственный за чистоту и порядок: {u.responsible}
        </p>
      )}

      {files('video').length > 0 && (
        <section className="stack" style={{ gap: 8 }}>
          <h2 className="section-title">Видео</h2>
          {files('video').map((f) => (
            <figure key={f.id} className="media-card">
              <AuthVideo path={f.url} size={f.size} />
              <figcaption className="row">
                <span className="grow">{f.title}</span>
                {manage && (
                  <button type="button" className="icon-btn" aria-label={`Удалить «${f.title}»`} onClick={() => void removeFile(f)}>
                    <Icon name="trash" size={16} />
                  </button>
                )}
              </figcaption>
            </figure>
          ))}
        </section>
      )}

      {files('photo').length > 0 && (
        <section className="stack" style={{ gap: 8 }}>
          <h2 className="section-title">Фото</h2>
          <div className="photo-grid">
            {files('photo').map((f) => (
              <button key={f.id} type="button" className="photo" aria-label={f.title} onClick={() => setPhoto(f)}>
                <AuthImg path={f.url} alt={f.title} />
              </button>
            ))}
          </div>
        </section>
      )}

      {files('doc').length > 0 && (
        <section className="stack" style={{ gap: 8 }}>
          <h2 className="section-title">Документы</h2>
          <div className="list">
            {files('doc').map((f) => (
              <div key={f.id} className="item">
                <span className="mark">
                  <Icon name="doc" />
                </span>
                <button type="button" className="grow stack plain" style={{ gap: 2 }} onClick={() => setDocOpen(f)}>
                  <span className="title ellipsis">{f.title}</span>
                  <span className="sub">PDF · {bytes(f.size)}</span>
                </button>
                {manage && (
                  <button type="button" className="icon-btn" aria-label={`Удалить «${f.title}»`} onClick={() => void removeFile(f)}>
                    <Icon name="trash" size={16} />
                  </button>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {u.procedures.length === 0 && u.files.length === 0 && (
        <p className="small muted" style={{ margin: 0 }}>
          {manage ? 'Списки действий, видео и фото добавьте кнопками ниже.' : 'Инструкций пока нет.'}
        </p>
      )}

      {manage && (
        <section className="stack" style={{ gap: 8 }}>
          <input
            ref={fileInput}
            type="file"
            accept="image/jpeg,image/png,image/webp,video/mp4,video/quicktime,video/webm,application/pdf"
            hidden
            onChange={(e) => void pickFile(e.target.files?.[0])}
          />
          <input ref={avatarInput} type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={(e) => void pickFile(e.target.files?.[0], true)} />
          {upload ? (
            <div className="card stack" style={{ gap: 8 }} role="status">
              <span className="small ellipsis">Загружаю {upload.name}…</span>
              <div className="progress">
                <i style={{ width: `${Math.round(upload.share * 100)}%` }} />
              </div>
            </div>
          ) : (
            <div className="grid-half">
              <button type="button" className="btn btn-outline" onClick={() => fileInput.current?.click()}>
                <Icon name="upload" size={18} />
                Видео, фото, PDF
              </button>
              <button type="button" className="btn btn-outline" onClick={() => setEditing(true)}>
                <Icon name="edit" size={18} />
                Название и панели
              </button>
              <button type="button" className="btn btn-outline" style={{ gridColumn: '1 / -1' }} onClick={() => setEditLists('all')}>
                <Icon name="tasks" size={18} />
                Списки действий
              </button>
              <button type="button" className="btn btn-outline" style={{ gridColumn: '1 / -1' }} onClick={() => avatarInput.current?.click()}>
                <Icon name="photo" size={18} />
                {u.avatarUrl ? 'Сменить главное фото' : 'Главное фото оборудования'}
              </button>
            </div>
          )}
          <QrBlock unit={u} />
        </section>
      )}

      {docOpen && <PdfViewer equipmentId={u.id} fileId={docOpen.id} title={docOpen.title} onClose={() => setDocOpen(null)} />}

      {photo && (
        <Sheet title={photo.title} onClose={() => setPhoto(null)}>
          <AuthImg path={photo.url} alt={photo.title} style={{ width: '100%', borderRadius: 12 }} />
          {manage && photo.id !== u.avatarFileId && (
            <button type="button" className="btn btn-outline btn-block" onClick={() => void makeAvatar(photo)}>
              <Icon name="star" size={18} />
              Сделать главным фото
            </button>
          )}
          {manage && (
            <button type="button" className="btn btn-danger btn-block" onClick={() => void removeFile(photo)}>
              Удалить фото
            </button>
          )}
        </Sheet>
      )}

      {step && !step.edit && (
        <Sheet title={`${step.proc.title} · ${step.n + 1}`} onClose={() => setStep(null)}>
          <p className="step-title">{step.item.text}</p>
          <StepContent equipmentId={u.id} details={step.item.details} fileIds={step.item.fileIds} files={allFiles} />
          {manage && (
            <button type="button" className="btn btn-outline btn-block" onClick={() => setStep({ ...step, edit: true })}>
              <Icon name="edit" size={18} />
              {step.item.details || step.item.fileIds?.length ? 'Изменить пункт' : 'Добавить «как сделать», фото или видео'}
            </button>
          )}
        </Sheet>
      )}
      {step?.edit && (
        <StepEditor
          equipmentId={u.id}
          item={step.item}
          files={allFiles}
          onFile={(f) => setNewFiles((l) => [...l, f])}
          onClose={() => setStep(null)}
          onDone={(item) => saveStep(step.proc, item)}
        />
      )}

      {editLists && (
        <ProceduresEditor
          unit={u}
          focus={editLists}
          onClose={() => setEditLists(false)}
          onSaved={() => {
            setEditLists(false);
            void reload(u.id);
          }}
        />
      )}

      {editing && (
        <EditUnit
          unit={u}
          areas={areas}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            void reload(u.id);
          }}
          onDeleted={close}
        />
      )}
    </div>
  );
}

/** Итог сегодняшней проверки: «✓ 08:12 Петров» или «замечания». */
export function CheckBadge({ c, onOpen }: { c: CheckDto; onOpen: () => void }) {
  return (
    <button type="button" className={`tchip ${c.status}`} onClick={onOpen}>
      <Icon name={c.status === 'issues' ? 'alert' : 'check'} size={14} />
      {c.status === 'issues' ? 'замечания' : 'пройдено'} · {clock(c.finishedAt ?? c.startedAt)} · {c.employeeName.split(' ')[0]}
    </button>
  );
}

/** Ссылки для QR-кода: новые наклейки — сразу в приложение; старые (?start=) работают через кнопку бота. */
function QrBlock({ unit }: { unit: EquipmentDetailDto }) {
  const { toast } = useApp();
  if (!unit.links.app) return null;
  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast('Ссылка скопирована');
    } catch {
      toast(text);
    }
  };
  return (
    <details className="card">
      <summary className="row" style={{ cursor: 'pointer' }}>
        <Icon name="qr" size={18} />
        <span className="grow">QR-код · {unit.qr}</span>
      </summary>
      <div className="stack" style={{ gap: 8, marginTop: 10 }}>
        <p className="small muted" style={{ margin: 0 }}>
          Для новой наклейки — ссылка, которая открывает эту страницу сразу. Старые коды (со ссылкой «?start=») тоже работают: бот ответит кнопкой.
        </p>
        <code className="small" style={{ wordBreak: 'break-all' }}>
          {unit.links.app}
        </code>
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => void copy(unit.links.app!)}>
          <Icon name="copy" size={16} />
          Скопировать ссылку
        </button>
      </div>
    </details>
  );
}

function EditUnit({
  unit,
  initial,
  areas,
  onClose,
  onSaved,
  onDeleted,
  inline,
}: {
  unit: EquipmentDetailDto | null;
  initial?: { code: string; qr: string };
  areas: AreaDto[];
  onClose: () => void;
  onSaved: (id: string) => void;
  onDeleted?: () => void;
  inline?: boolean;
}) {
  const { fail, toast, can } = useApp();
  const [name, setName] = useState(unit?.name ?? '');
  const [code, setCode] = useState(unit?.code ?? initial?.code ?? '');
  const [qr, setQr] = useState(unit?.qr ?? initial?.qr ?? '');
  const [qrTouched, setQrTouched] = useState(!!unit || !!initial?.qr);
  const [areaId, setAreaId] = useState(unit?.areaId ?? '');
  const [panelIds, setPanelIds] = useState<string[]>(unit?.panelIds ?? []);
  const [panels, setPanels] = useState<PanelDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    // Список панелей: администратору — все, остальным — видимые.
    (can('panel.manage') ? api.adminPanels() : can('panel.view') ? api.panels() : Promise.resolve([] as PanelDto[]))
      .then((list) => {
        setPanels(list);
        // Новое оборудование: сразу отмечаем панели, в названии которых есть заводской номер.
        if (!unit && code.trim()) setPanelIds(list.filter((p) => p.name.includes(code.trim())).map((p) => p.id));
      })
      .catch(() => setPanels([]));
    // Только при открытии формы.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const autoQr = qrKeyFromCode(code);
  const effectiveQr = qrTouched ? qr : autoQr;

  async function save() {
    const parsed = EquipmentInput.safeParse({
      name,
      code,
      qr: effectiveQr || null,
      areaId: areaId || null,
      panelIds,
    });
    if (!parsed.success) return setError(parsed.error.issues[0]?.message ?? 'Проверьте поля');
    setError(null);
    setBusy(true);
    try {
      const saved = unit ? await api.updateEquipment(unit.id, parsed.data) : await api.createEquipment(parsed.data);
      platform.haptic('success');
      toast(unit ? 'Сохранено' : 'Оборудование добавлено');
      onSaved(saved.id);
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!unit || !(await platform.confirm(`Удалить «${unit.name}» со всеми видео, фото и документами? Панели останутся.`))) return;
    setBusy(true);
    try {
      await api.deleteEquipment(unit.id);
      toast('Оборудование удалено');
      onDeleted?.();
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  }

  const sortedAreas = [...areas].sort((a, b) => areaPath(areas, a.id).localeCompare(areaPath(areas, b.id), 'ru'));
  const body = (
    <>
      <div className="field">
        <label htmlFor="un">Название</label>
        <input id="un" className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Пресс запрессовки подшипника" />
      </div>
      <div className="field">
        <label htmlFor="uc">Заводской номер</label>
        <input id="uc" className="input" value={code} autoCapitalize="characters" onChange={(e) => setCode(e.target.value)} placeholder="ШТ.20.01.01.02.000" />
      </div>
      <div className="field">
        <label htmlFor="uq">Ключ QR-кода</label>
        <input
          id="uq"
          className="input"
          value={effectiveQr}
          autoCapitalize="characters"
          autoCorrect="off"
          spellCheck={false}
          onChange={(e) => {
            setQrTouched(true);
            setQr(e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, ''));
          }}
        />
        <span className="hint">
          {unit ? 'Меняйте, только если на оборудовании наклеен код с другим ключом.' : 'Получается из заводского номера сам: ШТ.20.01.01.02.000 → S_20_01_01_02_000.'}
        </span>
      </div>
      <div className="field">
        <label htmlFor="ua">Участок</label>
        <select id="ua" className="input" value={areaId} onChange={(e) => setAreaId(e.target.value)}>
          <option value="">— без участка (видят только администраторы) —</option>
          {sortedAreas.map((a) => (
            <option key={a.id} value={a.id}>
              {areaPath(areas, a.id)}
            </option>
          ))}
        </select>
      </div>
      <div className="field">
        <span className="label">Панели этого оборудования</span>
        {!panels && <div className="skeleton" style={{ minHeight: 40 }} />}
        {panels && panels.length === 0 && <span className="hint">Панелей нет. Их добавляют в «Ещё → Участки и панели».</span>}
        {panels?.map((p) => (
          <label key={p.id} className="check">
            <input
              type="checkbox"
              checked={panelIds.includes(p.id)}
              onChange={(e) => setPanelIds((ids) => (e.target.checked ? [...ids, p.id] : ids.filter((x) => x !== p.id)))}
            />
            <span>
              {p.name}
              <span className="hint" style={{ display: 'block' }}>
                {areaPath(areas, p.areaId)}
                {p.equipmentId && p.equipmentId !== unit?.id ? ' · сейчас у другого оборудования' : ''}
              </span>
            </span>
          </label>
        ))}
      </div>
      {error && <p className="error">{error}</p>}
      <button type="button" className="btn btn-primary btn-block" disabled={busy} onClick={() => void save()}>
        {unit ? 'Сохранить' : 'Добавить оборудование'}
      </button>
      {unit && (
        <button type="button" className="btn btn-danger btn-block" disabled={busy} onClick={() => void remove()}>
          Удалить оборудование
        </button>
      )}
      {inline && (
        <button type="button" className="btn btn-ghost btn-block" onClick={onClose}>
          Отмена
        </button>
      )}
    </>
  );
  if (inline) return <div className="stack">{body}</div>;
  return (
    <Sheet title={unit ? unit.name : 'Новое оборудование'} onClose={onClose}>
      {body}
    </Sheet>
  );
}

